/**
 * Validación en runtime de config.json (training / soporte).
 * trazit-tracking #363 — availableLanguages + bot_ai
 */

const LANG_CODE_RE = /^[a-z]{2}(-[A-Za-z0-9]+)?$/;
const KNOWN_BOT_PROVIDERS = new Set(['gemini', 'openai', 'azure', 'anthropic', 'custom']);

function warn(prefix, message, detail) {
  if (detail !== undefined) {
    console.warn(`${prefix} ${message}`, detail);
  } else {
    console.warn(`${prefix} ${message}`);
  }
}

/**
 * @param {unknown} raw
 * @returns {{ ok: boolean, codes: string[], issues: string[] }}
 */
export function validateAvailableLanguages(raw) {
  const issues = [];
  if (raw == null || raw === '') {
    issues.push('availableLanguages vacío — se usará fallback en|es');
    return { ok: false, codes: [], issues };
  }
  if (typeof raw !== 'string') {
    issues.push(`availableLanguages debe ser string pipe-separated (recibido ${typeof raw})`);
    return { ok: false, codes: [], issues };
  }

  const codes = raw
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);

  if (codes.length === 0) {
    issues.push('availableLanguages sin códigos tras split por |');
    return { ok: false, codes: [], issues };
  }

  const seen = new Set();
  for (const code of codes) {
    if (!LANG_CODE_RE.test(code)) {
      issues.push(`código no válido: "${code}" (esperado es, en, pt-BR, …)`);
    }
    if (seen.has(code)) {
      issues.push(`código duplicado: "${code}"`);
    }
    seen.add(code);
  }

  return { ok: issues.length === 0, codes, issues };
}

/**
 * @param {unknown} botAi
 * @returns {{ ok: boolean, count: number, issues: string[] }}
 */
export function validateBotAi(botAi) {
  const issues = [];
  if (botAi == null) {
    return { ok: true, count: 0, issues };
  }
  if (!Array.isArray(botAi)) {
    issues.push(`bot_ai debe ser array (recibido ${typeof botAi})`);
    return { ok: false, count: 0, issues };
  }
  if (botAi.length === 0) {
    issues.push('bot_ai es array vacío');
    return { ok: false, count: 0, issues };
  }

  botAi.forEach((entry, idx) => {
    const label = `bot_ai[${idx}]`;
    if (!entry || typeof entry !== 'object') {
      issues.push(`${label}: entrada no es objeto`);
      return;
    }
    if (!entry.name && !entry.provider) {
      issues.push(`${label}: falta name o provider`);
    }
    if (entry.provider && !KNOWN_BOT_PROVIDERS.has(String(entry.provider).toLowerCase())) {
      issues.push(`${label}: provider desconocido "${entry.provider}"`);
    }
    if (entry.provider === 'gemini' && !entry.api_url) {
      issues.push(`${label}: gemini suele requerir api_url (proxy CORS en platform)`);
    }
    if (entry.access_key === 'TU_API_KEY_AQUI' || entry.access_key === 'YOUR_API_KEY') {
      issues.push(`${label}: access_key placeholder — sustituir antes de producción`);
    }
    if (entry.services && typeof entry.services !== 'object') {
      issues.push(`${label}: services debe ser objeto`);
    }
  });

  return { ok: issues.length === 0, count: botAi.length, issues };
}

/**
 * @param {object} config
 * @param {{ currentLang?: string, devMode?: boolean }} [options]
 */
export function validatePlatformConfigRuntime(config = {}, options = {}) {
  const prefix = '[TRAZiT config]';
  const langResult = validateAvailableLanguages(config.availableLanguages);
  for (const issue of langResult.issues) {
    warn(prefix, issue);
  }

  const botResult = validateBotAi(config.bot_ai);
  for (const issue of botResult.issues) {
    warn(prefix, issue);
  }

  const currentLang = options.currentLang || config.defaultLanguage;
  if (currentLang && langResult.codes.length && !langResult.codes.includes(currentLang)) {
    warn(
      prefix,
      `idioma activo "${currentLang}" no está en availableLanguages (${langResult.codes.join('|')})`,
    );
  }

  if (options.devMode && langResult.ok) {
    console.info(
      `${prefix} availableLanguages OK (${langResult.codes.length}): ${langResult.codes.join('|')}`,
    );
  }
  if (options.devMode && botResult.count > 0 && botResult.ok) {
    console.info(`${prefix} bot_ai OK (${botResult.count} proveedor/es)`);
  }

  return {
    availableLanguages: langResult,
    bot_ai: botResult,
    ok: langResult.ok && botResult.ok,
  };
}
